// One small global store (no library): session, balance, Director state + transcript (with artifact cards), live
// render and job progress, refresh signals from the Director's `ui.refresh`, toasts and the full-screen player.
import { useSyncExternalStore } from "react";
import { upsertArtifact, type Line } from "./chat";
import { idle as idlePlayback, type PlayState } from "./playback";
import { scrubDirectorText } from "./scrub";
import { closedStage, type StageState } from "./stage/state";
import type { Artifact, JobView, Me, Render } from "./types";
import { errorShown } from "./analytics";
import type { EndedState } from "./callend";

export type { Line } from "./chat";
export type DirectorStatus = "off" | "connecting" | "listening" | "thinking" | "speaking" | "error";
/** v4 (V4-U1): `action` = one button on the toast ("Undo"); tapping it runs `run` and closes the toast. */
export type Toast = { id: number; text: string; tone: "info" | "good" | "bad"; action?: { label: string; run: () => void } };

export type State = {
  auth: "unknown" | "in" | "out";
  me: Me | null;
  balance: number | null;
  director: DirectorStatus;
  directorError: string | null;
  /** what the Director is doing right now ("Writing the script"), shown under the iris while thinking */
  directorDoing: string | null;
  /** the voice switch in progress ("Eric"), so the iris can say so */
  directorSwitching: string | null;
  transcript: Line[];
  renders: Record<string, Render>; // live, by project id
  jobs: Record<string, JobView>; // live, by job id
  sceneImages: Record<string, Record<string, string>>; // projectId -> sceneId -> url
  refresh: Record<string, number>; // key -> tick
  toasts: Toast[];
  /** the audio card playing right now (by key), so only one plays at a time */
  playing: string | null;
  live: boolean; // SSE connected

  // ---- v2.1
  /** the user's mic mute (the call stays open; they can type) */
  micMuted: boolean;
  /** the Director is held quiet for playback (mic paused, volume 0) */
  directorQuiet: boolean;
  /** the playback hand-off (src/playback.ts) */
  playback: PlayState;
  /** the project open in the editing suite, so a `play` for it shows in the suite's preview, not the overlay */
  suiteProject: string | null;
  /** "Needs your review" items the user dismissed (keys from src/review.ts) */
  dismissed: string[];
  /** the user's last speech, typing or tap (the idle auto-end clock) */
  lastActivity: number;
  /**
   * the call ended without the user's own tap (one tap reconnects): after three quiet minutes, the Director hung up
   * (end_call), or the page went away / sat in the background (END-CALL, src/callend.ts)
   */
  directorEnded: EndedState | null;
  /** 2026-11-02: the open session is a typed chat (no mic, no voice): the UI says "Text chat", not "Listening" */
  directorTyped: boolean;
  /** per-scene model picks for the next clip, `${projectId}/${sceneId}` -> catalog id */
  sceneModel: Record<string, string>;

  // ---- v3
  /** the new-video sheet, when open: on a format's brief, or the picker with formats to highlight */
  newVideo: NewVideoRequest | null;
  /** the open Director call: when it connected, how long it may run, its price per minute and whether it's charged */
  directorCall: { startedAt: number; maxSeconds: number | null; creditsPerMin: number; charged: boolean } | null;
  /** the Director's price per minute as last seen (shown beside the call button before a call), and if it's charged */
  directorRate: { creditsPerMin: number; charged: boolean };
  /** 2026-11-02: a typed chat's lower rate (10 credits a minute until a chat's session says otherwise) */
  directorTextRate: number;
  /** why the last call couldn't start ("NO_CREDITS", "DIRECTOR_DAILY_LIMIT", "DIRECTOR_BUSY"...) */
  directorErrorCode: string | null;

  // ---- v4
  /** the Director's stage during a call (src/stage/state.ts): its items, focus, history and whether it's minimised */
  stage: StageState;
};
/** v4 (V4-H2): `channelId` / `collectionId` say where the new video goes (default: the main channel, no collection);
 *  `formats` limits the type picker to what a collection takes. */
export type NewVideoRequest = { format?: string; highlight?: string[]; channelId?: string | null; collectionId?: string | null; formats?: string[] };

const DISMISSED_KEY = "cao.review.dismissed";
const RATE_KEY = "cao.director.rate";
function loadDismissed(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(-200) : [];
  } catch {
    return [];
  }
}

let state: State = {
  auth: "unknown",
  me: null,
  balance: null,
  director: "off",
  directorError: null,
  directorDoing: null,
  directorSwitching: null,
  transcript: [],
  renders: {},
  jobs: {},
  sceneImages: {},
  refresh: {},
  toasts: [],
  playing: null,
  live: false,
  micMuted: false,
  directorQuiet: false,
  playback: idlePlayback(),
  suiteProject: null,
  dismissed: typeof localStorage === "undefined" ? [] : loadDismissed(),
  lastActivity: Date.now(),
  directorEnded: null,
  directorTyped: false,
  sceneModel: {},
  newVideo: null,
  directorCall: null,
  directorRate: loadRate(),
  directorTextRate: 10,
  directorErrorCode: null,
  stage: closedStage(),
};

/** The last Director rate this browser saw (30 credits a minute until the api says otherwise). */
function loadRate(): State["directorRate"] {
  try {
    const v = JSON.parse(localStorage.getItem(RATE_KEY) ?? "null");
    if (v && typeof v.creditsPerMin === "number" && v.creditsPerMin > 0) return { creditsPerMin: v.creditsPerMin, charged: v.charged !== false };
  } catch {
    /* no storage: the default */
  }
  return { creditsPerMin: 30, charged: true };
}
/** Remember the Director's price per minute (from POST /api/director/session). */
export function setDirectorRate(r: State["directorRate"]) {
  set({ directorRate: r });
  try {
    localStorage.setItem(RATE_KEY, JSON.stringify(r));
  } catch {
    /* private mode: this visit only */
  }
}

/** Open the new-video sheet (from a tap or the Director): on the type picker, or straight on one format's brief. */
export function openNewVideo(req: NewVideoRequest = {}) {
  set({ newVideo: req });
}
export const closeNewVideo = () => set({ newVideo: null });

/** Hide a "Needs your review" item (kept in this browser). */
export function dismissReview(key: string) {
  set((s) => ({ dismissed: [...s.dismissed.filter((k) => k !== key), key].slice(-200) }));
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(state.dismissed));
  } catch {
    /* private mode: dismissals last for this visit */
  }
}

/** The user spoke, typed or tapped (restarts the idle clock). */
export function markActivity() {
  state = { ...state, lastActivity: Date.now() };
}

const subs = new Set<() => void>();
export const get = () => state;
export function set(patch: Partial<State> | ((s: State) => Partial<State>)) {
  const p = typeof patch === "function" ? patch(state) : patch;
  state = { ...state, ...p };
  for (const s of subs) s();
}
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => subs.delete(f);
};
/** Runs `f` after every change (outside React: main.tsx keeps PostHog's user in step). Returns the unsubscribe. */
export const subscribeStore = subscribe;
export function useStore<T>(sel: (s: State) => T): T {
  return useSyncExternalStore(subscribe, () => sel(state), () => sel(state));
}

let lineId = 0;
const nextId = () => ++lineId;
/** Add a transcript line. The Director's lines lose their audio tags and emotion markers first; yours stay as typed. */
export function say(who: Line["who"], text: string) {
  const t = (who === "director" ? scrubDirectorText(text) : text).trim();
  if (!t) return;
  set((s) => ({ transcript: [...s.transcript.slice(-60), { id: nextId(), who, text: t, at: Date.now() }] }));
}

/** 2026-11-02 (chatpersist.ts): put back a saved chat, ahead of anything said since the page loaded; fresh ids. */
export function restoreTranscript(lines: Omit<Line, "id">[]) {
  if (!lines.length) return;
  set((s) => ({ transcript: [...lines.map((l) => ({ ...l, id: nextId() })), ...s.transcript].slice(-61) }));
}

/** Show (or update) an artifact card in the chat. */
export function showArtifact(a: Artifact) {
  set((s) => ({ transcript: upsertArtifact(s.transcript, a, nextId) }));
}

let toastId = 0;
export function toast(text: string, tone: Toast["tone"] = "info", action?: Toast["action"]) {
  const id = ++toastId;
  if (tone === "bad") errorShown(); // 4.0.1 PostHog: the error's code only (a no-op while it's off)
  set((s) => ({ toasts: [...s.toasts.slice(-2), { id, text, tone, ...(action ? { action } : {}) }] }));
  // a toast with a button stays a little longer, so there's time to tap it
  window.setTimeout(() => dismissToast(id), tone === "bad" || action ? 6000 : 3800);
}
export function dismissToast(id: number) {
  set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
}

/** Bump refresh keys ("projects", "project", "credits", "library", "brand", "me"); screens re-fetch on change. */
export function bump(...keys: string[]) {
  set((s) => {
    const r = { ...s.refresh };
    for (const k of keys) r[k] = (r[k] ?? 0) + 1;
    return { refresh: r };
  });
}
export const useRefresh = (...keys: string[]) => useStore((s) => keys.map((k) => s.refresh[k] ?? 0).join(","));
